import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';

import {
  FormikWrapper,
  FormEmail as EmailField,
  FormTextField as TextField,
  FormSelect as SelectField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  setOperationStatus,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
} from '@uncinc/react-drupal-core';

import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useOperationStatus from '../../hooks/useOperationStatus';
import { Button, Main } from '../../components';
import { inviteClient } from '../../store/actions/NursePractitioner';
import useToast from '../../hooks/useToast';

import './style.scss';

const PageInviteClient: React.FC = () => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const { t } = useTranslation();
  const { buildClass } = useClasses('PageInviteClient');
  const operationStatus = useOperationStatus('nursePractitioner', 'invite_client');
  const toast = useToast();

  React.useEffect(() => {
    if (operationStatus === UI_OPERATION_STATUS_SUCCESS) {
      toast.show(t('pages.invite_client.success_message'), 'success');
    }

    if (operationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.invite_client.error_message'), 'error');
    }

    if (operationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('nursePractitioner', 'invite_client', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [operationStatus, toast, t, dispatch]);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .required(t('pages.invite_client.fields.first_name.validation.required')),
    email: Yup.string()
      .required(t('pages.invite_client.fields.email.validation.required'))
      .email(t('pages.invite_client.fields.email.validation.email')),
    program: Yup.string()
      .required(t('pages.invite_client.fields.program.validation.required')),
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  function handleSubmit(values: FormValues, formik: FormikHelpers<FormValues>) {
    formik.setSubmitting(false);
    dispatch(inviteClient({
      first_name: values.firstName,
      email_address: values.email,
      domain: values.program,
    })).catch(() => {});
  }

  const initialValues: FormValues = {
    firstName: '',
    email: '',
    program: '',
  };

  return (
    <Page className={buildClass()} title={t('pages.invite_client.title')}>
      <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <Main className={buildClass('content')}>
          <h1 className={buildClass('title')}>
            {t('pages.invite_client.title')}
          </h1>
          <p className={buildClass('lead')}>
            {t('pages.invite_client.lead')}
          </p>
          <FormikWrapper
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
          >

            <TextField
              fieldName="firstName"
              title={t('pages.invite_client.fields.first_name.label')}
              placeholder={t('pages.invite_client.fields.first_name.placeholder')}
            />

            <EmailField
              fieldName="email"
              title={t('pages.invite_client.fields.email.label')}
              placeholder={t('pages.invite_client.fields.email.placeholder')}
            />

            <SelectField
              fieldName="program"
              title={t('pages.invite_client.fields.program.label')}
              placeholder={t('pages.invite_client.fields.program.placeholder')}
              isSearchable={false}
              options={{
                zelfhulpalcohol: t('domains:zelfhulpalcohol'),
                zelfhulptabak: t('domains:zelfhulptabak'),
                zelfhulpgokken: t('domains:zelfhulpgokken'),
                zelfhulpcannabis: t('domains:zelfhulpcannabis'),
                zelfhulpcocaine: t('domains:zelfhulpcocaine'),
              }}
            />

            <Button
              className={buildClass('submit')}
              htmlType="submit"
              buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
            >
              {t('pages.invite_client.submit')}
            </Button>
          </FormikWrapper>
        </Main>
      </div>
    </Page>
  );
};

export default PageInviteClient;
